import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { shiftApi } from "../../../api/resources";
import type { EmployeeOption } from "../../../api/types";
import { DemoBadge, TextInput, cn } from "../../../components/ui";

export function EmployeePicker({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const [options, setOptions] = useState<EmployeeOption[] | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    shiftApi.employeeOptions().then(setOptions).catch(() => setOptions([]));
  }, []);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = (options ?? []).filter((o) => !t || o.full_name.toLowerCase().includes(t));
    return [...list.filter((o) => o.eligible), ...list.filter((o) => !o.eligible)];
  }, [options, q]);
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div data-testid="employee-picker" className="rounded-sm border border-white/15">
      <div className="relative border-b border-white/10">
        <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-zinc-500" />
        <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees" className="border-0 pl-9 focus:ring-0" data-testid="employee-picker-search" />
      </div>
      <ul className="max-h-56 divide-y divide-white/5 overflow-y-auto">
        {options === null && <li data-testid="employee-picker-loading" className="px-4 py-3 text-sm text-zinc-500">Loading…</li>}
        {options && shown.length === 0 && <li data-testid="employee-picker-empty" className="px-4 py-3 text-sm text-zinc-500">No employees match.</li>}
        {shown.map((o) => (
          <li key={o.id}>
            <label data-testid={`employee-option-${o.id}`} aria-disabled={!o.eligible}
              className={cn("flex items-center gap-3 px-4 py-2.5 text-sm", o.eligible ? "cursor-pointer hover:bg-white/[0.04]" : "cursor-not-allowed opacity-45")}>
              <input type="checkbox" disabled={!o.eligible} checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="accent-signal" data-testid={`employee-option-checkbox-${o.id}`} />
              <span className="flex-1 text-white">{o.full_name} <DemoBadge show={o.is_demo} testId={`employee-option-demo-${o.id}`} /></span>
              {!o.eligible && <span data-testid={`employee-option-reason-${o.id}`} className="text-xs text-zinc-400">{o.reason}</span>}
            </label>
          </li>
        ))}
      </ul>
      <p data-testid="employee-picker-count" className="border-t border-white/10 px-4 py-2 text-xs text-zinc-400">{selected.length} selected · only Active employees can be assigned</p>
    </div>
  );
}
